import { Trans, useTranslation } from "react-i18next";

import { Button } from "src/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "src/core/components/ui/dialog";
import { CompareCustomerGraphWithExternalDataQuery } from "src/core/gql/graphql";

type CompareCustomerModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  compareData:
    | CompareCustomerGraphWithExternalDataQuery["compareCustomerGraphWithExternalData"]
    | undefined;
  onFolderAccess: () => void;
  onViewModifications: () => void;
};

export const CompareCustomerModal = ({
  open,
  setOpen,
  compareData,
  onFolderAccess,
  onViewModifications,
}: CompareCustomerModalProps) => {
  const stakeholdersWithDiffs = compareData?.stakeholdersWithDiffs ?? [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {stakeholdersWithDiffs.length === 0 ? (
        <NoDifferencesFoundModalContent onFolderAccess={onFolderAccess} />
      ) : (
        <DifferencesFoundModalContent
          onFolderAccess={onFolderAccess}
          onViewModifications={onViewModifications}
        />
      )}
    </Dialog>
  );
};

type NoDifferencesFoundModalContentProps = {
  onFolderAccess: () => void;
};

const NoDifferencesFoundModalContent = ({
  onFolderAccess,
}: NoDifferencesFoundModalContentProps) => {
  const { t } = useTranslation();

  return (
    <DialogContent hideCloseButton onPointerDownOutside={(e) => e.preventDefault()}>
      <DialogHeader className="mb-15">
        <DialogTitle>{t("modal.compareCustomer.noDifferencesTitle")}</DialogTitle>
        <DialogDescription className="sr-only"></DialogDescription>
      </DialogHeader>
      <div className="mb-15 whitespace-pre-line">
        <Trans
          i18nKey="modal.compareCustomer.noDifferencesDescription"
          components={{ strong: <strong /> }}
        />
      </div>
      <div className="flex justify-center">
        <Button onClick={onFolderAccess}>{t("modal.compareCustomer.folderAccess")}</Button>
      </div>
    </DialogContent>
  );
};

type DifferencesFoundModalContentProps = {
  onFolderAccess: () => void;
  onViewModifications: () => void;
};

const DifferencesFoundModalContent = ({
  onFolderAccess,
  onViewModifications,
}: DifferencesFoundModalContentProps) => {
  const { t } = useTranslation();
  return (
    <DialogContent hideCloseButton onPointerDownOutside={(e) => e.preventDefault()}>
      <DialogHeader className="mb-15">
        <DialogTitle>{t("modal.compareCustomer.differencesFoundTitle")}</DialogTitle>
        <DialogDescription className="sr-only"></DialogDescription>
      </DialogHeader>
      <div className="font-light whitespace-pre-line">
        {t("modal.compareCustomer.differencesFoundDescription")}
      </div>
      <div className="small-card mt-10 h-50 flex flex-col justify-start items-center">
        <div className="font-medium">{t("modal.compareCustomer.differencesFoundDescription2")}</div>
        <div className="font-light">{t("modal.compareCustomer.differencesFoundDescription3")}</div>
      </div>
      <div className="flex justify-between mt-20">
        <Button variant="link" onClick={onViewModifications}>
          {t("modal.compareCustomer.seeModifications")}
        </Button>
        <Button onClick={onFolderAccess}>{t("modal.compareCustomer.folderAccess")}</Button>
      </div>
    </DialogContent>
  );
};
